import { Location, LocationDTO, LocationMapper } from '@nx-smartmonkey/shared/domain';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal } from '../../../../../components/Modal';
import * as routing from '../../../../../constants/Routing';
import { DriverProjectFields } from '../../../../../domain/driversProject/DriversProject';
import { Route } from '../../../../../domain/route/Route';
import { ProjectDriversFieldTypes, RouteStopFieldProps, Stop } from '../../../../../domain/stops/Stop';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useRetrieveVehicle } from '../../../hooks/useRetrieveVehicle';
import { RouteActionType } from '../../context/actions';
import { useRouteContext } from '../../context/context';
import { EditStopSlidingMenu } from '../EditStopSlidingMenu';
import { AddStopsSlidingMenu } from './AddStopsSlidingMenu';
import { AddedStops } from './AddedStops';

export const CREATE_STOPS_INITIAL_LOCATION_KEY = `CREATE_STOPS_INITIAL_LOCATION`;

export const createStop = (location: LocationDTO, projectStopFields: DriverProjectFields, order: number): Stop => {
  const stopFields: RouteStopFieldProps[] = projectStopFields.map((field) => ({
    id: field.id,
    type: field.type as ProjectDriversFieldTypes,
    value: undefined,
    label: field.label,
    is_custom_field: field.is_custom_field,
  }));

  return Stop.create(
    {
      type: `delivery`,
      order,
      tasks: [],
      stop_fields: stopFields,
      status: `pending`,
      location: LocationMapper.toDomain(location),
    },
    uuidv4()
  );
};

interface CreateStopsProps {
  selectedRoute: Route;
  stops: Stop[];
  setStops: (stops: Stop[]) => void;
}

export const CreateStopsManually = ({ selectedRoute, stops, setStops }: CreateStopsProps) => {
  const navigate = useNavigate();
  const [customSearchParams] = useSearchParams();
  const { data: vehicleData } = useRetrieveVehicle();
  const { routeState, routeDispatch } = useRouteContext();
  const [addingStopsModalOpen, setAddingStopsModalOpen] = useState<boolean>(true);
  const [showAddedStops, setShowAddedStops] = useState<boolean>(stops.length > 0);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState<Stop | undefined>(undefined);
  const [duplicateStopsModal, setDuplicateStopsModal] = useState<LocationDTO | undefined>(undefined);

  useEffect(() => {
    (async () => {
      const initialLocation = localStorage.getItem(CREATE_STOPS_INITIAL_LOCATION_KEY);

      if (initialLocation) {
        setStops([
          createStop(JSON.parse(initialLocation), vehicleData?.project.fields.stop_fields ?? [], stops.length),
        ]);
      } else {
        // if (stops.length === 0) navigate(routing.ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
      }
      setAddingStopsModalOpen(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fromMap = customSearchParams.get(`from_map`);
    if (fromMap) {
      setShowAddedStops(true);
      setAddingStopsModalOpen(false);
    }
  }, [customSearchParams]);

  const manageAddLocation = (location: Location) => {
    if (
      [...selectedRoute.stops, ...stops].find(
        (s) =>
          s.location &&
          s.location.lng &&
          s.location.lat &&
          location.lng === s.location.lng &&
          location.lat === s.location.lat
      )
    ) {
      setDuplicateStopsModal(location);
    } else {
      setStops([...stops, createStop(location, vehicleData?.project.fields.stop_fields ?? [], stops.length)]);
    }
  };

  return (
    <>
      <Modal
        open={confirmDeleteModal !== undefined}
        title={{
          intlId: `driver.stop.delete_edit.title`,
        }}
        subtitle={{
          intlId: `driver.stop.delete_edit.info`,
        }}
        buttons={[
          {
            intlId: `driver.stop.delete_edit.confirm`,
            onClick: () => {
              setStops(stops.filter((s: Stop) => s.id !== confirmDeleteModal!.id));
              setConfirmDeleteModal(undefined);
            },
            style: {
              backgroundColor: RoutalPalette.error.medium,
              borderColor: RoutalPalette.error.medium,
              color: RoutalPalette.white,
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setConfirmDeleteModal(undefined);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <Modal
        open={duplicateStopsModal !== undefined}
        onClose={() => {
          setDuplicateStopsModal(undefined);
        }}
        title={{
          intlId: `driver.stops.create_stops.add_duplicate_stop.title`,
        }}
        subtitle={{
          text: intl.formatMessage(
            {
              id: `driver.stops.create_stops.add_duplicate_stop.subtitle`,
            },
            { value: duplicateStopsModal?.label }
          ),
        }}
        buttons={[
          {
            intlId: `driver.stops.create_stops.confirm`,
            onClick: () => {
              setStops([
                ...stops,
                createStop(duplicateStopsModal!, vehicleData?.project.fields.stop_fields ?? [], stops.length),
              ]);
              setDuplicateStopsModal(undefined);
            },
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setDuplicateStopsModal(undefined);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      {routeState.editingStop && (
        <EditStopSlidingMenu
          open
          slideStyles={{ zIndex: 1008 }}
          editingStop={routeState.editingStop}
          onRemoveStop={(stop: Stop) => {
            setStops(stops.filter((s: Stop) => s.id !== stop.id));
            routeDispatch({
              type: RouteActionType.SET_EDITING_STOP,
              payload: undefined,
            });
          }}
          onSaveChanges={(stop: Stop) => {
            setStops(stops.map((s: Stop) => (s.id === stop.id ? stop : s)));
            routeDispatch({
              type: RouteActionType.SET_EDITING_STOP,
              payload: undefined,
            });
          }}
          onExit={() => {
            routeDispatch({
              type: RouteActionType.SET_EDITING_STOP,
              payload: undefined,
            });
          }}
        />
      )}

      <AddStopsSlidingMenu
        open={addingStopsModalOpen}
        slideStyles={{ zIndex: addingStopsModalOpen ? 1007 : 1006 }}
        addedStops={stops}
        onRemoveStop={(stop) => {
          setConfirmDeleteModal(stop);
        }}
        onAddLocation={(location) => {
          manageAddLocation(location);
        }}
        onClose={() => {
          setShowAddedStops(true);
          localStorage.removeItem(CREATE_STOPS_INITIAL_LOCATION_KEY);
          setAddingStopsModalOpen(false);
        }}
        onExit={() => {
          localStorage.removeItem(CREATE_STOPS_INITIAL_LOCATION_KEY);
          navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
          setAddingStopsModalOpen(false);
          setStops([]);
        }}
      />

      {showAddedStops && (
        <AddedStops
          selectedRoute={selectedRoute}
          addedStops={stops}
          onOpenAddMoreStops={() => {
            setAddingStopsModalOpen(true);
            setShowAddedStops(false);
          }}
          onAddLocation={(location) => {
            manageAddLocation(location);
          }}
          onCreatedAndOptimized={() => {
            setStops([]);
          }}
        />
      )}
    </>
  );
};
